import * as React from "react"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  PerformanceRowList,
  PerformanceRowsSkeleton,
  toneForHealth,
  useBudgetPerformance,
  type PerformanceRow,
} from "@/features/finance/overview/widgets/budget-performance-shared"

const MAX_ROWS = 5

/**
 * Budget vs Actual (expense) — planned against realised per EXPENSE ACCOUNT, worst utilization first.
 *
 * Account performance, not fund state: these figures come from transactions posted to each account's budget
 * lines, so money later redesignated between funds does not move them.
 */
export function BudgetVsActualWidget() {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const query = useBudgetPerformance()

  const rows = React.useMemo<PerformanceRow[] | undefined>(() => {
    if (!query.data) return undefined
    return [...query.data.byExpenseAccount]
      .sort((a, b) => (b.utilizationPct ?? 0) - (a.utilizationPct ?? 0))
      .slice(0, MAX_ROWS)
      .map((account) => ({
        id: account.accountId,
        label: account.accountName ?? account.accountId,
        planned: account.planned,
        actual: account.actual,
        utilizationPct: account.utilizationPct,
        tone: toneForHealth(account.healthStatus),
      }))
  }, [query.data])

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.budgets) ? () => go(OverviewRoutes.budgets) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={rows}
      isEmpty={(list) => list.length === 0}
      skeleton={<PerformanceRowsSkeleton />}
      errorTitle={t("widgets.budgetVsActual.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.budgetVsActual.empty.title")}
    >
      {(list) => <PerformanceRowList rows={list} currency={query.data?.currency ?? ""} />}
    </WidgetBoundary>
  )
}
